import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { authApi } from '../../services/api/authApi';
import { handleApiError } from '../../services/api/errorHandler';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBackToLogin: () => void;
}

interface FormData {
  email: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
}

type Step = 'email' | 'reset';

export default function ForgotPasswordModal({ 
  isOpen, 
  onClose, 
  onBackToLogin 
}: ForgotPasswordModalProps): JSX.Element {
  const [step, setStep] = useState<Step>('email');
  const [formData, setFormData] = useState<FormData>({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [maskedEmail, setMaskedEmail] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };

  const handleSendCode = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await authApi.forgotPassword(formData.email);
      
      if (response.success) {
        setMaskedEmail(response.destination || formData.email);
        setSuccessMessage(response.message);
        setStep('reset');
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password strength
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }

    try {
      const response = await authApi.resetPassword(
        formData.email,
        formData.code,
        formData.newPassword
      );
      
      if (response.success) {
        setSuccessMessage(response.message);
        // Auto-close after success and return to login
        setTimeout(() => {
          handleClose();
          onBackToLogin();
        }, 2000);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (): void => {
    onClose();
    // Reset form state
    setStep('email');
    setFormData({ email: '', code: '', newPassword: '', confirmPassword: '' });
    setError('');
    setSuccessMessage('');
    setMaskedEmail('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleResendCode = async (): Promise<void> => {
    setIsLoading(true);
    setError('');

    try {
      const response = await authApi.forgotPassword(formData.email);
      
      if (response.success) {
        setSuccessMessage('Verification code resent to your email');
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog as="div" className="relative z-50" open={isOpen} onClose={handleClose}>
      <div className="fixed inset-0 bg-black bg-opacity-25" />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title
                as="h3"
                data-testid="forgot-password-modal-title"
                className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100"
              >
                {step === 'email' ? 'Reset Password' : 'Enter New Password'}
              </Dialog.Title>
              <button
                data-testid="forgot-password-close-button"
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {step === 'email' ? (
              <form onSubmit={handleSendCode} className="space-y-4" data-testid="forgot-password-email-form">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Enter your email address and we'll send you a verification code to reset your password.
                  </p>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    data-testid="forgot-password-email-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter your email"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div 
                    data-testid="forgot-password-error-message"
                    className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                  >
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                  </div>
                )}

                {/* Success Message */}
                {successMessage && (
                  <div 
                    data-testid="forgot-password-success-message"
                    className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
                  >
                    <p className="text-sm text-green-800 dark:text-green-200">{successMessage}</p>
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="send-reset-code-button"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Code'}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    data-testid="back-to-login-button"
                    onClick={onBackToLogin}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500"
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    We've sent a verification code to <strong data-testid="masked-email">{maskedEmail}</strong>. 
                    Enter the code and your new password below.
                  </p>
                </div>

                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Verification Code
                  </label>
                  <input
                    id="code"
                    name="code"
                    type="text"
                    required
                    data-testid="verification-code-input"
                    value={formData.code}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                  />
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="newPassword"
                      name="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      required
                      data-testid="new-password-input"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      data-testid="toggle-new-password-visibility"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Confirm New Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      data-testid="confirm-password-input"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      data-testid="toggle-confirm-password-visibility"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div 
                    data-testid="reset-password-error-message"
                    className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                  >
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                  </div>
                )}

                {/* Success Message */}
                {successMessage && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <p className="text-sm text-green-800 dark:text-green-200">{successMessage}</p>
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="reset-password-submit-button"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>

                <div className="flex justify-between text-sm">
                  <button
                    type="button"
                    data-testid="resend-code-button"
                    onClick={handleResendCode}
                    disabled={isLoading}
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-500 disabled:opacity-50"
                  >
                    Resend Code
                  </button>
                  <button
                    type="button"
                    data-testid="change-email-button"
                    onClick={() => setStep('email')}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-500"
                  >
                    Change Email
                  </button>
                </div>
              </form>
            )}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
